import clsx from 'clsx';
import React from 'react';
import { Checkbox as ARIACheckbox, type SlotProps } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';

export interface CheckboxProps extends SlotProps {
    /**
     * HTML id of the checkbox.
     */
    id?: string;

    /**
     * The value of the checkbox.
     */
    value?: boolean;

    /**
     * If true, the checkbox is disabled.
     * @default false
     */
    disabled?: boolean;

    /**
     * If true, the checkbox is read-only.
     * @default false
     */
    readOnly?: boolean;

    /**
     * Callback when the checkbox value changes.
     */
    onChange?: (value: boolean) => void;

    /**
     * The size of the checkbox.
     */
    size?: 'small' | 'medium';

    /**
     * Callback a keystroke is registered on the checkbox input.
     */
    onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;

    className?: string;
}

export type CheckboxRef = HTMLLabelElement;

/**
 * Checkbox component for forms with built-in accessibility support.
 */
export const Checkbox = React.forwardRef<CheckboxRef, CheckboxProps>((props, ref) => {
    const {
        id,
        value,
        disabled = false,
        readOnly = false,
        onChange,
        size = 'medium',
        onKeyDown,
        className,
        ...rest
    } = props;

    return (
        <ARIACheckbox
            {...rest}
            ref={ref}
            id={id}
            isSelected={value}
            isDisabled={disabled}
            isReadOnly={readOnly}
            onChange={onChange}
            onKeyDown={onKeyDown}
            className={({ isSelected, isIndeterminate, isDisabled, isReadOnly }) =>
                clsx(
                    'flex shrink-0 items-center justify-center border data-[focus-visible]:shadow-focus-ring',
                    'relative', // Ensure that when the hidden input is in a different location as the actual visual checkbox, the DOM scrolls to the correct position
                    { small: 'size-4 rounded', medium: 'size-5 rounded-md' }[size],
                    isDisabled
                        ? 'cursor-not-allowed opacity-disabled'
                        : isReadOnly
                          ? 'cursor-default'
                          : 'cursor-pointer',
                    isSelected || isIndeterminate
                        ? 'border-primary bg-primary text-white'
                        : isDisabled
                          ? 'border-[var(--sp-color-border-form-radio-unchecked-disabled)] bg-inactive'
                          : 'border-gray-300 bg-base',
                    className
                )
            }
        >
            {({ isSelected, isIndeterminate }) =>
                isIndeterminate ? '-' : <Icon.AnimatedCheck isSelected={isSelected} />
            }
        </ARIACheckbox>
    );
});
